/**
 * Formats upload validation errors for the admin quiz panel (CSV + OMR JSON).
 */

/** Prefer API error body over axios generic "Request failed with status code 400". */
export function extractApiErrorMessage(error) {
  if (!error) return 'Something went wrong';
  const data = error.response && error.response.data;
  if (data) {
    if (typeof data === 'string') return data;
    if (data.message) return data.message;
    if (data.data && data.data.summary) return data.data.summary;
    if (Array.isArray(data.errors) && data.errors[0]) {
      return data.errors[0].error || data.errors[0].message || data.errors[0];
    }
  }
  if (!error.response && (error.message === 'Network Error' || error.code === 'ERR_NETWORK')) {
    return 'Upload interrupted — the server connection was lost. Wait a few seconds and try again.';
  }
  if (error.message && !/^Request failed with status code \d+$/.test(error.message)) {
    return error.message;
  }
  if (error.response && error.response.status) {
    return `Request failed (${error.response.status})`;
  }
  return error.message || 'Something went wrong';
}

const CSV_HINTS = {
  question: 'Enter the full question text in the "question" column for that row.',
  option: 'Each option needs text in option1–option4 (or an equation in equation1–equation4).',
  correct: 'Mark exactly one option as correct: set is_correct1, is_correct2, is_correct3, or is_correct4 to TRUE. All others should be FALSE or empty.',
  two_options: 'Every question needs at least two filled options.',
  header: 'Download the sample CSV and copy its header row exactly. For MCQ quizzes required columns include ind_number, question, option1–option4, is_correct1–is_correct4. For DAQ use question_type_level (long|short|numerical), total_marks, and taxonomy columns (configurations, topics, sub_topics).',
  empty: 'Add at least one question row under the header row.',
};

function guessCsvHint(message) {
  const m = String(message || '').toLowerCase();
  if (m.includes('header') || m.includes('column')) return CSV_HINTS.header;
  if (m.includes('empty') && m.includes('question')) return CSV_HINTS.question;
  if (m.includes('option')) return CSV_HINTS.option;
  if (m.includes('correct')) return CSV_HINTS.correct;
  if (m.includes('two options')) return CSV_HINTS.two_options;
  if (m.includes('empty')) return CSV_HINTS.empty;
  return 'Fix the issue described, save the CSV, and upload again. Compare with the sample CSV file.';
}

/** Parse "error # message # row # hint" lines from CSV socket */
export function parseCsvValidationLines(lines) {
  const errors = [];
  const warnings = [];

  (lines || []).forEach((line) => {
    if (!line || typeof line !== 'string') return;
    const parts = line.split('#').map((p) => p.trim());
    if (parts.length < 2) return;

    const kind = parts[0].toLowerCase();
    const message = parts[1] || 'Validation issue';
    const row = parts[2] || '—';
    const hint = parts[3] || guessCsvHint(message);

    if (kind === 'error') {
      errors.push({ no: row, error: message, hint });
    } else if (kind === 'warning') {
      warnings.push({ no: row, warning: message, hint });
    }
  });

  return {
    errors,
    warnings,
    summary: buildUploadSummary(errors, warnings, 'CSV'),
  };
}

export function parseCsvSocketFatalError(raw) {
  if (!raw) {
    return {
      summary: 'CSV upload failed.',
      errors: [{ no: 'File', error: 'Unknown error', hint: CSV_HINTS.header }],
      warnings: [],
    };
  }

  if (typeof raw === 'object') {
    return {
      summary: raw.message || 'CSV upload failed.',
      errors: [{
        no: 'File',
        error: raw.message || 'CSV upload failed.',
        hint: raw.hint || CSV_HINTS.header,
      }],
      warnings: [],
    };
  }

  try {
    const parsed = JSON.parse(raw);
    return parseCsvSocketFatalError(parsed);
  } catch (e) {
    const text = String(raw);
    return {
      summary: text,
      errors: [{
        no: 'File',
        error: text,
        hint: guessCsvHint(text),
      }],
      warnings: [],
    };
  }
}

export function applyOmrImportFailure(responseData, httpStatus, options = {}) {
  if (options.isNetworkError || (!httpStatus && responseData && responseData.message === 'Network Error')) {
    return {
      errors: [{
        no: 'Server',
        error: 'Connection lost while uploading the JSON file',
        hint: 'The quiz API may have restarted. Wait a few seconds, refresh the page, and upload again.',
      }],
      warnings: [],
      summary: extractApiErrorMessage({ message: 'Network Error', response: null }),
    };
  }

  if (httpStatus === 401) {
    const message = (responseData && responseData.message) || 'Unauthorized';
    return {
      errors: [{
        no: 'Auth',
        error: message,
        hint: 'Log out and log in again. Your session may have expired.',
      }],
      warnings: [],
      summary: 'Import failed: you are not authorized. Please log in again.',
    };
  }

  if (typeof responseData === 'string') {
    const isAuth = /unauthorized|401/i.test(responseData);
    return {
      errors: [{
        no: isAuth ? 'Auth' : 'File',
        error: responseData,
        hint: isAuth
          ? 'Log out and log in again.'
          : 'Download the sample OMR JSON and ensure your file matches its structure.',
      }],
      warnings: [],
      summary: isAuth
        ? 'Import failed: you are not authorized. Please log in again.'
        : responseData,
    };
  }

  const data = (responseData && responseData.data) || responseData || {};
  const errors = Array.isArray(data.errors) ? data.errors : [];
  const warnings = Array.isArray(data.warnings) ? data.warnings : [];

  if (!errors.length && responseData && responseData.message) {
    errors.push({
      no: 'File',
      error: responseData.message,
      hint: 'Download the sample OMR JSON and ensure your file matches its structure.',
    });
  }

  if (!errors.length && !warnings.length) {
    errors.push({
      no: 'File',
      error: 'Import failed',
      hint: 'Check your connection, log in again, and re-upload the JSON file.',
    });
  }

  return {
    errors: errors.map(normalizeIssue),
    warnings: warnings.map((w) => ({
      no: w.no || '—',
      warning: w.warning || w.error || String(w),
      hint: w.hint || '',
    })),
    summary: data.summary || responseData.message || buildUploadSummary(errors, warnings, 'JSON'),
  };
}

export function applyOmrImportSuccess(data) {
  const warnings = (data && data.warnings) || [];
  return {
    errors: [],
    warnings: warnings.map((w) => ({
      no: w.no || '—',
      warning: w.warning || String(w),
      hint: w.hint || '',
    })),
    summary: warnings.length
      ? `Imported successfully with ${warnings.length} warning(s) — review below.`
      : 'All questions imported successfully.',
  };
}

function normalizeIssue(item) {
  return {
    no: item.no != null ? item.no : '—',
    error: item.error || item.message || String(item),
    hint: item.hint || '',
    field: item.field || '',
  };
}

export function buildUploadSummary(errors, warnings, fileType) {
  const errCount = (errors || []).length;
  const warnCount = (warnings || []).length;
  if (!errCount && !warnCount) {
    return `Your ${fileType} file passed all checks.`;
  }
  if (errCount && warnCount) {
    return `Your ${fileType} has ${errCount} error(s) to fix and ${warnCount} warning(s). Correct the errors below, update the file, and upload again.`;
  }
  if (errCount) {
    return `Your ${fileType} has ${errCount} error(s) and was not imported. Fix each issue below, save the file, and re-upload.`;
  }
  return `Your ${fileType} imported with ${warnCount} warning(s). We recommend fixing them for a better student experience.`;
}

export function applyUploadResultToVm(vm, { summary, errors, warnings }) {
  vm.uploadErrorSummary = summary || '';
  vm.errors = errors || [];
  vm.warnings = warnings || [];
}
